import { Head, Link, router } from "@inertiajs/react"
import {
  Video,
  ExternalLink,
} from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { SectionTabs } from "../components/SectionTabs"

const articlesTabs = [
  { label: "All Articles", href: "/app/admin/articles" },
  { label: "Pending Review", href: "/app/admin/articles/pending_review" },
  { label: "Meetings", href: "/app/admin/meetings" },
]
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import { MeetingStatusBadge } from "@/components/meetings/MeetingStatusBadge"

function formatDate(dateString) {
  if (!dateString) return "-"
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

const filterOptions = [
  { label: "Total", value: null, key: "total" },
  { label: "Active", value: "active", key: "active" },
  { label: "Awaiting Claim", value: "awaiting_claim", key: "awaiting_claim" },
  { label: "Completed", value: "completed", key: "completed" },
  { label: "Failed", value: "failed", key: "failed" },
]

export default function MeetingsIndex({
  currentUser,
  meetings,
  stats,
  currentStatus,
  pendingCount,
}) {
  const handleFilter = (status) => {
    router.get(
      "/app/admin/meetings",
      status ? { status } : {},
      { preserveState: true }
    )
  }

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title="Meetings - Admin - Kingdom Factor" />

      <div className="space-y-6">
        <SectionTabs tabs={articlesTabs} />

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Meetings</h1>
          <p className="text-zinc-600">
            All meeting recordings across coaches
          </p>
        </div>

        {/* Stats Filter Cards */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {filterOptions.map((opt) => {
            const isActive = currentStatus === opt.value ||
              (!currentStatus && opt.value === null)
            return (
              <button
                key={opt.key}
                onClick={() => handleFilter(opt.value)}
                className={`p-4 rounded-lg border text-left transition-colors ${
                  isActive
                    ? "border-kf-red bg-kf-red/5"
                    : "border-zinc-200 bg-white hover:border-zinc-300"
                }`}
              >
                <p className="text-sm text-zinc-600">{opt.label}</p>
                <p className="text-2xl font-bold">{stats?.[opt.key] || 0}</p>
              </button>
            )
          })}
        </div>

        {/* Meetings Table */}
        <Card>
          <CardContent className="p-0">
            {meetings?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="hidden md:table-cell">Date</TableHead>
                    <TableHead className="hidden lg:table-cell">Coach</TableHead>
                    <TableHead>Interviewee</TableHead>
                    <TableHead className="hidden md:table-cell">Source</TableHead>
                    <TableHead className="hidden sm:table-cell">Status</TableHead>
                    <TableHead>Article</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {meetings.map((meeting) => (
                    <TableRow key={meeting.id}>
                      <TableCell className="text-zinc-600 hidden md:table-cell">
                        {formatDate(meeting.createdAt)}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div>
                          <p className="font-medium text-zinc-900 text-sm">
                            {meeting.coach?.name}
                          </p>
                          {meeting.coach?.name !== meeting.coach?.email && (
                            <p className="text-xs text-zinc-500">
                              {meeting.coach?.email}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium whitespace-normal">
                        <div className="min-w-0">
                          <p className="line-clamp-1">{meeting.intervieweeName || "-"}</p>
                          {meeting.intervieweeCompany && (
                            <span className="text-zinc-500 text-sm block line-clamp-1">
                              {meeting.intervieweeCompany}
                            </span>
                          )}
                          <p className="text-xs text-zinc-500 lg:hidden truncate mt-0.5">
                            {meeting.coach?.name}
                          </p>
                          <div className="mt-1.5 flex items-center gap-1.5 sm:hidden">
                            <MeetingStatusBadge status={meeting.status} />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge variant="secondary" className="text-xs">
                          {meeting.source === "recall_bot" ? "Bot" : "Upload"}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <MeetingStatusBadge status={meeting.status} />
                      </TableCell>
                      <TableCell>
                        {meeting.articleId ? (
                          <Link
                            href={`/app/admin/articles/${meeting.articleId}`}
                            className="text-kf-red hover:underline text-sm flex items-center gap-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            <span className="truncate max-w-[160px]">
                              {meeting.articleTitle || "View Article"}
                            </span>
                          </Link>
                        ) : meeting.errorMessage ? (
                          <span className="text-sm text-red-600 truncate max-w-[200px] block">
                            {meeting.errorMessage}
                          </span>
                        ) : (
                          <span className="text-zinc-400 text-sm">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <div className="w-14 h-14 rounded-full bg-zinc-100 flex items-center justify-center mb-4">
                  <Video className="w-7 h-7 text-zinc-400" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-1">
                  No meetings yet
                </h3>
                <p className="text-zinc-600 text-center max-w-sm">
                  Meetings will appear here when coaches start recording interviews.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  )
}
